import EventEmitter from 'events';

/**
 * @description Подписчик.
 * @param {EventEmitter} emitter
 * @param {any}          config
 * @constructor
 */
function Subscriber(emitter, config) {
  let queue = [];
  /** @type {ServiceWorker|null} */
  this.worker = null;

  /**
   * @description Подключиться.
   * @param {boolean} force
   * @returns {NotificationSubscriber}
   */
  this.connect = (force = false) => {
    if (this.worker === null) {
      queue.push(() => this.connect(force));
      return this;
    }
    const token = config.resolveToken();
    this.worker.postMessage({ type: 'connect', data: { host: config.host, token, force } });
    return this;
  };

  /**
   * @description Подписаться.
   * @param {string}   event
   * @param {function} listener
   * @returns {NotificationSubscriber}
   */
  this.subscribe = (event, listener) => {
    emitter.on(event, listener);
    return this;
  };

  /**
   * @description Отписаться.
   * @param {string} event
   * @param {function} listener
   * @returns {NotificationSubscriber}
   */
  this.unsubscribe = (event, listener) => {
    emitter.off(event, listener);
    return this;
  };

  /**
   * @description Установить воркер.
   * @param {ServiceWorker} newWorker
   * @returns {void}
   */
  this.setWorker = (newWorker) => {
    this.worker = newWorker;
    if (queue.length) {
      queue.forEach((command) => { command(); });
      queue = [];
    }
  };

  this.showNotification = (title, options = {}, key = null) => {
    Notification.requestPermission().then((status) => {
      if (status === 'granted') {
        const optionsWithIcon = { icon: '/favicon.png', ...options };
        this.worker.postMessage({ type: 'notification', data: { key, title, optionsWithIcon } });
      }
    });
  };

  this.disconnect = () => {
    if (this.worker) {
      this.worker.postMessage({ type: 'disconnect' });
    }
  };
}

/**
 * @description Плагин серверных сообщений.
 * @constructor
 * @type {VuePlugin}
 */
export default function ServerMessagePlugin(config, logger = console) {
  const debug = (...message) => {
    if (config.debug) {
      logger.debug(...message);
    }
  };

  /**
   * @inheritDoc
   */
  this.install = (Vue) => {
    const emitter = new EventEmitter();
    const subscriber = new Subscriber(
      emitter,
      {
        host: config.host,
        resolveToken: config.resolveToken,
      },
    );
    if ('serviceWorker' in navigator) {
      const ping = () => {
        if (subscriber.worker) {
          subscriber.worker.postMessage({ type: 'ping' });
        }
      };
      let pingIntervalTimerId = null;
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
        subscriber.setWorker(registration.active);
        if (pingIntervalTimerId) {
          clearInterval(pingIntervalTimerId);
        }
        pingIntervalTimerId = setInterval(ping, 25000);
      });
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { type, data } = event.data;
        if (type === 'message') {
          emitter.emit(data.data.event, data.data);
        }
        if (type === 'refresh') {
          if (config.resolveIsAuthenticated()) {
            event.source.postMessage({ type: 'lock', data: { key: data.key } });
          }
        }
        if (type === 'locked') {
          debug('Получена блокировка', data.key);
          if ((data.key || '').startsWith('refresh')) {
            emitter.emit('refresh');
          }
        }
        if (type === 'status') {
          debug('Статус подключения', data);
        }
      });
      navigator.serviceWorker.addEventListener('controllerchange', (event) => {
        debug('switch to next version');
        subscriber.setWorker(event.target.controller);
        subscriber.connect();
      });
      document.addEventListener('visibilitychange', () => {
        ping();
      });
    }
    Object.defineProperty(Vue.prototype, '$serverMessages', { value: subscriber });
  };
}
